import type { ViewHint } from "@/content/schema";

export interface Vec3 {
	x: number;
	y: number;
	z: number;
}
export interface CameraPose {
	position: Vec3;
	target: Vec3;
}

export const DEFAULT_VIEW: ViewHint = { az: 35, el: 20, min: 1.0 };

/** Vertical fov (deg) for a camera defined by its horizontal fov and the viewport aspect. */
export function verticalFov(hfovDeg: number, aspect: number): number {
	return (
		(2 * Math.atan(Math.tan((hfovDeg * Math.PI) / 180 / 2) / aspect) * 180) /
		Math.PI
	);
}

/**
 * Camera pose that frames a bounding sphere (centre, radius) from a viewpoint.
 * az: azimuth in degrees around +Y (0 = in front of the product, which faces +Z in glTF space), el: elevation.
 * Distance is chosen so the sphere fits in the narrower of the two fovs, with a 12 % margin; the camera never goes
 * below `minHeight` (metres above the floor).
 */
export function framePose(
	center: Vec3,
	radius: number,
	view: ViewHint,
	vfovDeg: number,
	aspect: number,
	minHeight = 0.08,
): CameraPose {
	const r = Math.max(radius, view.min / 2);
	const vfov = (vfovDeg * Math.PI) / 180;
	const hfov = 2 * Math.atan(Math.tan(vfov / 2) * aspect);
	const dist = (r / Math.sin(Math.min(vfov, hfov) / 2)) * 1.12;
	const a = (view.az * Math.PI) / 180;
	const e = (view.el * Math.PI) / 180;
	const dir = {
		x: Math.sin(a) * Math.cos(e),
		y: Math.sin(e),
		z: Math.cos(a) * Math.cos(e),
	};
	const position = {
		x: center.x + dir.x * dist,
		y: Math.max(minHeight, center.y + dir.y * dist),
		z: center.z + dir.z * dist,
	};
	return { position, target: { ...center } };
}

/** Sample fractions of a step used to measure where the moving parts are (start and end of the gesture included). */
export const FRAMING_SAMPLES = [0, 0.15, 0.35, 0.6, 0.85, 1] as const;

/** Ease-out cubic used for the camera transition. */
export const easeOutCubic = (t: number): number =>
	1 - (1 - Math.min(1, Math.max(0, t))) ** 3;
